import React, { useContext, useEffect, useState } from "react";
import { Button, TextField, useMediaQuery, useTheme } from '@mui/material';
import { IAuthResult, IChallenge, RequestStatus, api } from "../../logic/api";
import { SessionContext } from "../../utility/useSession";
import AnimateFrom from "../../components/animations/AnimateFrom";
import Scaler from "../dash/components/scaler/Scaler";
import { useTranslation } from "react-i18next";
import { TenantSelector } from "./TenantSelector";

import styles from './styles';
import { FaSpinner } from "react-icons/fa";
import { SnackAlert } from "../../components/notifications/snack/SnackAlert";
import LangSwitcher from '../../i18n/lang-switcher';

export interface ILoginPageV2Props {
}

export const LoginPageV2: React.FC<ILoginPageV2Props> = () => {
  const session = useContext(SessionContext);

  const [sessionId, setSessionId] = useState<string>("");
  const [currentState, setCurrentState] = useState<string>("start");
  const [authParameters, setAuthParameters] = useState<Record<string, string>>({});
  const [authResult, setAuthResult] = useState<IAuthResult | null>(null);

  const handleReset = () => {
    session.logout();
    setCurrentState("start");
  };

  const handleAuthComplete = (result: IAuthResult) => {
    session.login(result.token, {
      firstName: result.firstName,
      lastName: result.lastName,
      database: "",
      identity: "",
      clientCode: "",
      twoFactorEnabled: result.twoFactorEnabled
    })
    setCurrentState("select_tenant");
    setAuthResult(result);
  };

  const handleAuthChallenge = (result: IChallenge) => {
    // The auth session ID is set using a cookie in the response headers
    if (result.challengeMethod === "sso_redirect") {
      window.location.href = result.parameters.uri;
      return;
    }

    setSessionId(result.sessionId);
    setAuthParameters({ ...authParameters, ...result.parameters });
    setCurrentState(result.challengeMethod);
  };

  return <>
    {(["start", "password"].includes(currentState)) && (
      <InitAuthForm onAuthComplete={handleAuthComplete} onAuthChallenge={handleAuthChallenge} onReset={handleReset} authParameters={authParameters} sessionId={sessionId} />
    )}

    {(["sms_code"].includes(currentState)) && (
      <TwoFactorForm onAuthComplete={handleAuthComplete} onAuthChallenge={handleAuthChallenge} onReset={handleReset} authParameters={authParameters} sessionId={sessionId} />
    )}

    {(["change_password"].includes(currentState)) && (
      <ChangePasswordForm onAuthComplete={handleAuthComplete} onAuthChallenge={handleAuthChallenge} onReset={handleReset} authParameters={authParameters} sessionId={sessionId} />
    )}

    {(["select_tenant"].includes(currentState) && authResult) && (
      <TenantSelector onReset={handleReset} />
    )}
  </>
};

interface LoginTitleProps {
  title: string;
  message: string;
}

export const LoginTitle: React.FC<LoginTitleProps> = ({
  title,
  message,
}) => {
  return <div style={styles.row}>
    <AnimateFrom style={{ opacity: 0 }}>
      <Scaler>
        <h1 style={styles.title}>{title}</h1>
        <p style={styles.message}>{message}</p>
      </Scaler>
    </AnimateFrom>
  </div>
}

interface IUserFormProps {
  authParameters: Record<string, string>;
  sessionId: string;
  onAuthComplete: (result: IAuthResult) => void;
  onAuthChallenge: (result: IChallenge) => void;
  onReset: () => void;
}

const InitAuthForm: React.FC<IUserFormProps> = (props) => {
  const session = useContext(SessionContext);
  const { t, i18n } = useTranslation();
  const theme = useTheme();

  const initAuthentication = api.hooks.auth.initAuthentication();
  const respondToChallenge = api.hooks.auth.respondToChallenge();

  const [state, setState] = useState<string>("start");
  const [buttonText, setButtonText] = useState<string>(t("general.next"));


  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;

    if (state === "start") {
      const username = form.username?.value;
      initAuthentication.send(
        session.current.context,
        { username },
        (result) => {
          if (!result) {
            return;
          }

          if (result.authResult) {
            props.onAuthComplete(result.authResult);
            return;
          }

          props.onAuthChallenge(result.challenge);

          if (result.challenge.challengeMethod === "password") {
            setState("password");
          }
        }
      );
    } else {
      const password = form.password?.value;

      respondToChallenge.send(
        session.current.context,
        {
          challengeMethod: "password",
          sessionId: props.sessionId,
          parameters: { password }
        },
        (result) => {
          if (!result) {
            return;
          }

          if (result.authResult) {
            props.onAuthComplete(result.authResult);
            return;
          }

          props.onAuthChallenge(result.challenge);
        }
      );
    }
  }

  const handleReset = () => {
    setState("start");
  }

  const isLargeDevice = useMediaQuery(theme.breakpoints.up('md'));
  const fieldStyle = isLargeDevice == true ? styles.field : { ...styles.field, ...styles.fieldSm };
  const isBusy = initAuthentication.status === RequestStatus.loading || respondToChallenge.status === RequestStatus.loading;

  useEffect(() => {
    setButtonText(state === "start" ? t("general.next") : t("login.intro.signInButton"));
  }, [i18n.language]);

  const handleNext = () => {
    setButtonText(t("login.intro.signInButton"));
  }

  return <>
    <LoginTitle title={t("login.intro.title")} message={t("login.intro.message")} />

    <form id="login" onSubmit={handleSubmit} onReset={handleReset}>
      <AnimateFrom delay={1.5} style={{ opacity: 0, x: 100 }}>
        <div style={styles.row}>
          <TextField
            style={fieldStyle}
            id="login-input"
            name="username"
            label={t("login.intro.usernameFieldLabel")}
            type="text"
            size="small"
            autoComplete="username"
            variant="outlined"
            className="loginInput"
            disabled={state === "password"}
            autoFocus={state === "start"}
            required={true}
            slotProps={{
              htmlInput: {
                "data-testid": "login-username-input"
              }
            }}
          />
        </div>

        {state !== "start" && (
          <div style={styles.row}>
            <TextField
              style={fieldStyle}
              id="password-input"
              name="password"
              label={t("login.intro.passwordFieldLabel")}
              type="password"
              size="small"
              autoComplete="password"
              variant="outlined"
              className="loginInput"
              autoFocus={state === "password"}
              required={true}
              slotProps={{
                htmlInput: {
                  "data-testid": "login-password-input"
                }
              }}
            />
          </div>
        )}

        <div style={styles.row}>
          <div style={isLargeDevice ? styles.alignRight : styles.alignRightSm}>
            <LangSwitcher />
          </div>
        </div>
      </AnimateFrom>

      <div style={styles.row}>
        <div style={isLargeDevice ? styles.alignRight : styles.alignRightSm}>
          <AnimateFrom inline={true} delay={1.75} style={{ opacity: 0, x: 100 }}>
            <Button type="submit" disabled={isBusy} style={styles.buttons.primary} variant="contained" color="secondary" onClick={handleNext} data-testid="login-submit-button">
              {buttonText}
              {isBusy && (<FaSpinner className="spin" style={{ marginLeft: 5 }} />)}
            </Button>
          </AnimateFrom>

          {state === "password" && (
            <Button type="reset" disabled={isBusy} style={styles.buttons.secondary} variant="outlined" color="secondary">{t("general.cancel")}</Button>
          )}
        </div>
      </div>
    </form>

    <SnackAlert id="loginError" message={initAuthentication.err || respondToChallenge.err} ></SnackAlert >
  </>
}


interface ITwoFactorFormProps extends IUserFormProps { }

const TwoFactorForm: React.FC<ITwoFactorFormProps> = (props) => {
  const session = useContext(SessionContext);
  const { t } = useTranslation();
  const theme = useTheme();

  const respondToChallenge = api.hooks.auth.respondToChallenge();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const code = form.twoFactor?.value;

    respondToChallenge.send(
      session.current.context,
      {
        challengeMethod: "sms_code",
        sessionId: props.sessionId,
        parameters: {
          twoFactorCode: code
        },
      },
      (result) => {
        if (!result) {
          return;
        }

        if (result.authResult) {
          props.onAuthComplete(result.authResult);
          return;
        }

        props.onAuthChallenge(result.challenge);
      }
    );
  }

  const isLargeDevice = useMediaQuery(theme.breakpoints.up('md'));
  const fieldStyle = isLargeDevice == true ? styles.field : { ...styles.field, ...styles.fieldSm };
  const isBusy = respondToChallenge.status === RequestStatus.loading;
  const codeNotAccepted = respondToChallenge.err != null;

  return <div>
    <LoginTitle
      title={t("login.twoFactor.title", { user: "" })}
      message={t("login.twoFactor.message", { phoneMask: props.authParameters.phone })}
    />

    <form id="twoFactor" onSubmit={handleSubmit} onReset={props.onReset}>
      <AnimateFrom style={{ opacity: 0, x: 100 }}>
        {codeNotAccepted && (
          <div style={styles.row}>
            <span style={styles.errorRow}>{t("login.twoFactor.codeNotAccepted")}</span>
          </div>
        )}

        <div style={styles.row}>
          <TextField
            style={fieldStyle}
            id="twoFactor"
            label={t("login.twoFactor.twoFactorFieldLabel")}
            type="text"
            size="small"
            autoComplete="off"
            variant="outlined"
            className="loginInput"
            autoFocus={true}
            required={true}
          />
        </div>
      </AnimateFrom>

      <div style={{ marginTop: 20 }}></div>

      <div style={isLargeDevice ? styles.alignRight : styles.alignRightSm}>
        <AnimateFrom inline={true} delay={1} style={{ opacity: 0, x: 100 }}>
          <Button type="submit" disabled={isBusy} style={styles.buttons.primary} variant="contained" color="secondary">
            {t("general.confirm")}
            {isBusy && (<FaSpinner className="spin" style={{ marginLeft: 5 }} />)}
          </Button>
        </AnimateFrom>

        <AnimateFrom inline={true} delay={1.5} style={{ opacity: 0, x: 100 }}>
          <Button type="reset" disabled={isBusy} style={styles.buttons.secondary} variant="outlined" color="secondary">{t("general.cancel")}</Button>
        </AnimateFrom>
      </div>
    </form>
  </div>;
}


interface IChangePasswordFormProps extends IUserFormProps { }
const ChangePasswordForm: React.FC<IChangePasswordFormProps> = (props) => {
  const { t } = useTranslation();
  const session = useContext(SessionContext);
  const theme = useTheme();

  const respondToChallenge = api.hooks.auth.respondToChallenge();

  const [passwordMismatch, setPasswordMismatch] = useState<boolean>(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setPasswordMismatch(false);

    const form = e.currentTarget;
    const newPassword = form.newPassword?.value;
    const confirmPassword = form.confirmPassword?.value;

    console.log({ newPassword, confirmPassword });
    if (!newPassword || !confirmPassword || newPassword !== confirmPassword) {
      setPasswordMismatch(true);
      return;
    }

    respondToChallenge.send(
      session.current.context,
      {
        challengeMethod: "change_password",
        sessionId: props.sessionId,
        parameters: {
          newPassword,
          confirmPassword
        }
      },
      (result) => {
        if (!result) {
          return;
        }

        if (result.authResult) {
          props.onAuthComplete(result.authResult);
          return;
        }

        props.onAuthChallenge(result.challenge);
      }
    );
  }

  const isLargeDevice = useMediaQuery(theme.breakpoints.up('md'));
  const fieldStyle = isLargeDevice == true ? styles.field : { ...styles.field, ...styles.fieldSm };

  const isBusy = respondToChallenge.status === RequestStatus.loading;
  const passwordRejected = respondToChallenge.err != null;

  return <div>
    <LoginTitle title={t("login.site.title", { name: props.authParameters.firstName })} message={t("login.changedPassword.message")} />

    <form id="changepassword" onSubmit={handleSubmit} onReset={props.onReset}>
      <AnimateFrom style={{ opacity: 0, x: 100 }}>
        {(passwordMismatch) && (
          <div style={styles.row}>
            <span style={styles.errorRow}>{String(respondToChallenge.err)}</span>
          </div>
        )}

        {(passwordRejected) && (
          <div style={styles.row}>
            <span style={styles.errorRow}>{String(respondToChallenge.err)}</span>
          </div>
        )}

        <div style={styles.row}>
          <TextField
            style={fieldStyle}
            id="newPassword"
            label={t("login.changedPassword.newPasswordFieldLabel")}
            type="password"
            size="small"
            autoComplete="off"
            variant="outlined"
            className="loginInput"
            autoFocus={true}
            required
          />
        </div>

        <div style={styles.row}>
          <TextField
            style={fieldStyle}
            id="confirmPassword"
            label={t("login.changedPassword.confirmNewPasswordFieldLabel")}
            type="password"
            size="small"
            autoComplete="off"
            variant="outlined"
            className="loginInput"
            required
          />
        </div>
      </AnimateFrom>

      <div style={{ marginTop: 20 }}></div>

      <div style={isLargeDevice ? styles.alignRight : styles.alignRightSm}>
        <AnimateFrom inline={true} delay={1} style={{ opacity: 0, x: 100 }}>
          <Button type="submit" disabled={isBusy} style={styles.buttons.primary} variant="contained" color="secondary">
            {t("general.confirm")}
            {isBusy && (<FaSpinner className="spin" style={{ marginLeft: 5 }} />)}
          </Button>
        </AnimateFrom>

        <AnimateFrom inline={true} delay={1.5} style={{ opacity: 0, x: 100 }}>
          <Button type="reset" disabled={isBusy} style={styles.buttons.secondary} variant="outlined" color="secondary">{t("general.cancel")}</Button>
        </AnimateFrom>
      </div>
    </form>
  </div>
}
